import {
  del,
  get,
  patch,
  post,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ApiListResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import {
  buildDonationDonorChildPayload,
  type AddDonationPayload,
  type DonationDonorChildPayload,
  type DonationDonorPatchPayload,
  type DonationDonorPayload,
  type DonationDonorRecord,
} from "@project/types/donation-donor";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  unwrapItem,
} from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/donation-master`;
const SILENT: RequestConfig = { showNotification: false };

function extractDonorList(
  data:
    | ApiListResponse<DonationDonorRecord>
    | DonationDonorRecord[]
    | { data?: DonationDonorRecord[]; results?: DonationDonorRecord[] }
    | undefined
): DonationDonorRecord[] {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if ("data" in data && Array.isArray(data.data)) {
    return data.data;
  }
  if ("results" in data && Array.isArray(data.results)) {
    return data.results;
  }
  return [];
}

class DonationDonorService {
  listPage = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<DonationDonorRecord>> =>
    fetchListPage<DonationDonorRecord>(`${BASE}/`, query);

  create = async (
    payload: DonationDonorPayload
  ): Promise<DonationDonorRecord> => {
    const data = await post<
      ApiItemResponse<DonationDonorRecord> | DonationDonorRecord,
      DonationDonorPayload
    >(`${BASE}/`, payload, SILENT);
    const item = unwrapItem(data);
    if (!item) {
      throw new Error("Failed to create donor record.");
    }
    return item;
  };

  update = async (
    id: number | string,
    payload: DonationDonorPatchPayload
  ): Promise<DonationDonorRecord | undefined> => {
    const data = await patch<
      ApiItemResponse<DonationDonorRecord> | DonationDonorRecord,
      DonationDonorPatchPayload
    >(`${BASE}/${id}/`, payload, SILENT);
    return unwrapItem(data);
  };

  get = async (
    id: number | string
  ): Promise<DonationDonorRecord | undefined> => {
    const data = await get<
      ApiItemResponse<DonationDonorRecord> | DonationDonorRecord
    >(`${BASE}/${id}/`, SILENT_LIST);
    return unwrapItem(data);
  };

  delete = async (id: number | string): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };

  /** Remove a child donor by PATCHing the parent with the updated children array. */
  removeChild = async (
    parentId: number | string,
    childId: number,
    parent: DonationDonorRecord
  ): Promise<DonationDonorRecord | undefined> => {
    const children: DonationDonorChildPayload[] = (parent.children ?? [])
      .filter(child => child.id !== childId)
      .map(buildDonationDonorChildPayload);
    return this.update(parentId, { children });
  };

  searchByPhone = async (
    phone: string
  ): Promise<DonationDonorRecord | null> => {
    try {
      const data = await get<
        | ApiListResponse<DonationDonorRecord>
        | DonationDonorRecord[]
        | { data?: DonationDonorRecord[]; results?: DonationDonorRecord[] }
      >(`${BASE}/`, {
        ...SILENT_LIST,
        params: { search: phone },
      });
      const list = extractDonorList(data);
      return list.length > 0 ? list[0] : null;
    } catch {
      return null;
    }
  };
}

class DonationAddService {
  addDonation = async (payload: AddDonationPayload): Promise<unknown> => {
    return post(`${JIVANDHARA_BASE}/donations/add/`, payload, SILENT);
  };
}

export const donationDonorService = new DonationDonorService();
export const donationAddService = new DonationAddService();
